/**
 * @description NotificationService module.
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module services/NotificationService
 */

import HeatmapNotification from './classes/Notification';
import CONSTANTS from '../config/Constants';

/**
 * @description A class that handles notifications.
 * @class
 */
class NotificationService {

    /**
     * Creates an instance of NotificationService.
     * @param {Function} [onNotificationsUpdate=() => null] - (Optional) handler for when notifications are updated.
     */
    constructor(onNotificationsUpdate = () => null) {
        this.idIterator = this.idGenerator();
        this.onNotificationsUpdate = onNotificationsUpdate;
    }

    /**
     * @description Adds a notification.
     * @param {Object} notificationDetails - The notification details.
     * @returns The created notification.
     */
    addNotification(notificationDetails) {
        const notification = new HeatmapNotification(
            this.idIterator.next().value,
            notificationDetails
        );

        notifications.push(notification);
        this.onNotificationsUpdate(this.getNotifications());

        if (notificationDetails.type === 'toast') {
            // setTimeout to only show a toast notification for a set time.
            setTimeout(
                () => this.removeNotification(notification.id),
                CONSTANTS.TOAST_DISPLAY_TIME
            );
        }

        notificationDetails.additionalInformation &&
            console.log(notificationDetails.additionalInformation);

        return notification.getNotification();
    }

    /**
     * @description Removes a notification by id.
     * @param {number} id - The notification id.
     * @returns The deleted notification.
     */
    removeNotification(id) {
        const index = notifications.findIndex(
            notification => notification.id === id
        );

        if (index < 0) {
            return null;
        }

        const notification = notifications.splice(index, 1)[0];

        notification.handleClose();
        this.onNotificationsUpdate(this.getNotifications());

        return notification.getNotification();
    }

    /**
     * @description Gets all notifications.
     * @returns A list of all notifications.
     */
    getNotifications() {
        return notifications.map(notification =>
            notification.getNotification()
        );
    }

    /**
     * @description Gets a notification by id.
     * @param {number} id - The notification id.
     * @returns The notification.
     */
    getNotificationById(id) {
        return notifications
            .find(notification => notification.id === id)
            .getNotification();
    }

    /**
     * @description Triggers the action of a notification.
     * @param {number} id - The id of the notification.
     */
    triggerNotificationAction(id) {
        const notification = notifications.find(
            notification => notification.id === id
        );

        notification.handleAction();
        this.removeNotification(notification.id);
    }

    /**
     * @description A generator function to create unique ids.
     */
    *idGenerator() {
        let id = 0;

        while (true) {
            yield id++;
        }
    }
}

const notifications = [];

export default NotificationService;
